import { getFirstName, getLastName, isPhoneNumber } from '@bybeam/formatting';
import validatePassword from '@bybeam/platform-lib/authentication/validatePassword';
import { isEmail } from '@platform-ui-common/utilities/email.js';
import dayjs from 'dayjs';
import {
  FieldCondition,
  FieldValidator,
  GenericFormData,
  SimpleValidationFieldConfig,
} from './types.js';

export function defaultRequiredErrorMessage(fieldName: string): string {
  return `${fieldName} is required`;
}

export function requiredField<FormData extends GenericFormData>(
  validate?: FieldValidator<FormData>,
  requiredErrorMessage?: string,
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required: true,
    validate,
    ...(requiredErrorMessage && { requiredErrorMessage }),
  };
}

export function optionalField<FormData extends GenericFormData>(
  validate?: FieldValidator<FormData>,
): SimpleValidationFieldConfig<FormData> {
  return { type: 'simple', required: false, validate };
}

export function conditionallyRequiredField<FormData extends GenericFormData>(
  validate?: FieldValidator<FormData>,
  condition?: FieldCondition<FormData>,
  requiredErrorMessage?: string,
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required: true,
    validate,
    condition,
    ...(requiredErrorMessage && { requiredErrorMessage }),
  };
}

export function emailValidation<FormData extends GenericFormData>(
  field: keyof FormData,
): FieldValidator<FormData> {
  return ({ [field]: email }): string | undefined =>
    !isEmail(email) && 'Please enter a valid email address';
}

export function emailField<FormData extends GenericFormData>(
  required = true,
  field: keyof FormData = 'email',
  fieldName = 'Email Address',
  condition?: FieldCondition<FormData>,
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required,
    validate: emailValidation(field),
    requiredErrorMessage: defaultRequiredErrorMessage(fieldName),
    condition,
  };
}

export function phoneValidation<FormData extends GenericFormData>(
  field: keyof FormData,
): FieldValidator<FormData> {
  return ({ [field]: phone }): string | undefined =>
    !isPhoneNumber(`${phone}`) && 'Please enter a valid phone number';
}

export function phoneField<FormData extends GenericFormData>(
  required = true,
  field: keyof FormData = 'phone',
  fieldName = 'Phone Number',
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required,
    validate: phoneValidation(field),
    requiredErrorMessage: defaultRequiredErrorMessage(fieldName),
  };
}

export function passwordField<FormData extends { password: string }>(
  required = true,
  field: keyof FormData = 'password',
  fieldName = 'Password',
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required,
    validate: ({ [field]: password }): string[] | undefined => {
      const validations = validatePassword(password as string).map(({ validation }) => validation);
      return validations.length > 0 ? validations : undefined;
    },
    requiredErrorMessage: defaultRequiredErrorMessage(fieldName),
  };
}

export function confirmPasswordField<
  FormData extends { password: string; confirmPassword: string },
>(required = true): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required,
    validate: ({ password, confirmPassword }): string | undefined =>
      password !== confirmPassword && 'Must match password',
    requiredErrorMessage: defaultRequiredErrorMessage('Confirm Password'),
  };
}

export function dateValidation<FormData extends GenericFormData>(
  field: keyof FormData,
  options: {
    allowPastDates?: boolean;
    allowFutureDates?: boolean;
  } = { allowPastDates: true, allowFutureDates: true },
  fieldName = 'Date',
): FieldValidator<FormData> {
  return (formData: FormData) => {
    const { [field]: value } = formData;
    if (!dayjs(value).isValid()) return 'Enter a valid date';

    const parsedDate = dayjs(value);
    if (!options.allowPastDates && !parsedDate.isSameOrAfter(new Date(), 'day'))
      return `${fieldName} may not be in the past`;
    if (!options.allowFutureDates && !parsedDate.isSameOrBefore(new Date(), 'day'))
      return `${fieldName} may not be in the future`;
    if (parsedDate.year() < 1900) return `${fieldName} must be after 1900`;

    const tenYearsFromNow = dayjs().year() + 10;
    if (parsedDate.year() > tenYearsFromNow)
      return `${fieldName} must be before ${tenYearsFromNow}`;
  };
}

export function dateField<FormData extends GenericFormData>(
  field: keyof FormData,
  required = true,
  options: {
    allowPastDates?: boolean;
    allowFutureDates?: boolean;
  } = { allowPastDates: true, allowFutureDates: true },
  fieldName = 'Date',
  additionalValidation?: FieldValidator<FormData>,
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required,
    validate: (formData: FormData): string | string[] | true | undefined =>
      dateValidation(field, options, fieldName)(formData) || additionalValidation?.(formData),
  };
}

export function characterCountValidation(
  maxCharacters: number,
  fieldName: string,
  value?: string | number,
): string | undefined {
  if ((value ?? '').toString().length > maxCharacters)
    return `${fieldName} exceeds the character limit. Please reduce the number of characters.`;
}

export function minCharacterCountValidation({
  minCharacters,
  fieldName,
  value,
}: {
  minCharacters: number;
  fieldName: string;
  value?: string | number;
}): string | undefined {
  const input = (value ?? '').toString().length;
  if (input > 0 && input < minCharacters)
    return `${fieldName} does not meet the character minimum. Please increase the number of characters.`;
}

export function exactCharacterCountValidation({
  exact,
  fieldName,
  value,
}: {
  exact?: number | null;
  fieldName: string;
  value?: string | number;
}): string | undefined {
  if (!exact) return;
  const inputLength = (value ?? '').toString().length;
  if (inputLength !== 0 && inputLength !== exact)
    return `${fieldName} must be exactly ${exact} characters long`;
}

export function numberValidation(value: number | undefined): boolean {
  return Number.isNaN(Number(value)) || Number(value) < 0;
}

export function numberField<FormData extends GenericFormData>(
  field: keyof FormData,
  required = true,
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required,
    validate: ({ [field]: numField }): string | undefined =>
      numberValidation(numField) && 'Amount cannot be lower than 0',
  };
}

export function checkboxFieldIsPresent<FormData extends GenericFormData>(field: keyof FormData) {
  return ({ [field]: value }): boolean => !!value;
}

export function checkboxField<FormData extends GenericFormData>(
  field: keyof FormData,
  requiredErrorMessage = 'This checkbox is required',
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required: true,
    requiredErrorMessage,
    fieldIsPresent: checkboxFieldIsPresent(field),
  };
}

export function fullNameField<FormData extends GenericFormData>(
  field = 'fullName',
  required = true,
  checkParts = true,
): SimpleValidationFieldConfig<FormData> {
  return {
    type: 'simple',
    required,
    validate: ({ [field]: nameField }): string | undefined => {
      if (!nameField.match(/^[a-zA-ZÀ-ÿ]+[-a-zA-ZÀ-ÿ0-9'’& ]*$/u))
        return 'Please enter only valid characters';
      if (checkParts) {
        const userFirstName = getFirstName(nameField);
        const userLastName = getLastName(nameField);
        if (!userFirstName || !userLastName) return 'Please enter first and last name';
      }
      if (nameField.length <= 4) return 'Full Name must be at least 4 characters long';
      return undefined;
    },
    requiredErrorMessage: defaultRequiredErrorMessage('Full Name'),
  };
}
